import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CopyBox } from "@/components/CopyBox";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Eye, Users, MousePointerClick, Percent, Plus, Globe, Code2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#1C4358", RED = "#9C1E22", SKY = "#9BB9CE", GREEN = "#2F7D5A";
const COLORS = [NAVY, RED, SKY, "#C4C89B", GREEN, "#B7791F", "#252221"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A` }}><Icon size={19} style={{ color }} /></span>
      <div className="min-w-0"><div className="text-2xl font-bold font-display tabular-nums text-[#252221] leading-none">{value}</div><div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div></div>
    </div>
  </Card>
);

export default function MarketingAnalytics() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("all");
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newSite, setNewSite] = useState({ name: "", domain: "" });
  const [snippet, setSnippet] = useState(null);

  const loadSites = useCallback(async () => {
    const { data } = await api.get("/marketing/sites"); setSites(data.items || []);
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (siteId !== "all") params.site_id = siteId;
      const { data } = await api.get("/marketing/analytics", { params });
      setData(data);
    } finally { setLoading(false); }
  }, [siteId, days]);

  useEffect(() => { loadSites(); }, [loadSites]);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const createSite = async () => {
    if (!newSite.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/marketing/sites", newSite);
      toast.success("Website connected");
      setAddOpen(false); setNewSite({ name: "", domain: "" }); loadSites();
    } catch { toast.error("Could not add website"); }
  };

  const showSnippet = async (site) => {
    try {
      const { data } = await api.get(`/marketing/sites/${site.id}/snippet`, { params: { base: BACKEND } });
      setSnippet({ site, ...data });
    } catch { toast.error("Could not load snippet"); }
  };

  const removeSite = async (site) => {
    await api.delete(`/marketing/sites/${site.id}`); toast.success("Removed"); loadSites();
  };

  const series = data?.series || [];
  const sources = data?.sources || [];
  const topPages = data?.top_pages || [];

  return (
    <div className="space-y-6" data-testid="marketing-analytics">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#252221]">Website Analytics</h1>
          <p className="text-sm text-muted-foreground">First-party traffic &amp; conversion tracking from your marketing site.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="w-[160px]" data-testid="analytics-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[120px]" data-testid="analytics-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Eye} label="Pageviews" value={data?.pageviews ?? 0} color={NAVY} testid="analytics-pageviews" />
          <Stat icon={Users} label="Visitors" value={data?.visitors ?? 0} color={SKY} testid="analytics-visitors" />
          <Stat icon={MousePointerClick} label="Conversions" value={data?.conversions ?? 0} color={RED} testid="analytics-conversions" />
          <Stat icon={Percent} label="Conversion rate" value={`${data?.conversion_rate ?? 0}%`} color={GREEN} testid="analytics-conv-rate" />
        </div>
      )}

      <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <h3 className="font-display font-semibold text-[#252221] mb-3">Pageviews over time</h3>
        {series.length === 0 ? <p className="text-sm text-muted-foreground py-12 text-center">No traffic yet. Install the tracking snippet on your site to start collecting data.</p> : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={series} margin={{ left: -10 }}>
              <defs><linearGradient id="pv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={NAVY} stopOpacity={0.35} /><stop offset="100%" stopColor={NAVY} stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#4B5563" }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
              <Tooltip cursor={{ stroke: SKY }} />
              <Area type="monotone" dataKey="views" stroke={NAVY} strokeWidth={2} fill="url(#pv)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <h3 className="font-display font-semibold text-[#252221] mb-3">Traffic sources</h3>
          {sources.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={sources} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
                <YAxis type="category" dataKey="source" tick={{ fontSize: 12, fill: "#4B5563" }} width={90} />
                <Tooltip cursor={{ fill: "rgba(155,185,206,0.12)" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>{sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <h3 className="font-display font-semibold text-[#252221] mb-3">Top pages</h3>
          {topPages.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data yet.</p> : (
            <div className="divide-y divide-[#F0F4F7]">
              {topPages.map((p, i) => (
                <div key={i} className="py-2.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-[#252221] truncate">{p.path}</span>
                  <span className="text-sm font-semibold tabular-nums text-[#1C4358]">{p.views}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Connected sites */}
      <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-[#252221]">Connected websites</h3>
          <Button onClick={() => setAddOpen(true)} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="add-site-button"><Plus size={15} className="mr-1.5" /> Connect website</Button>
        </div>
        {sites.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No websites connected yet.</p> : (
          <div className="space-y-2">
            {sites.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[#E6EDF2] p-3" data-testid="site-row">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><Globe size={17} className="text-[#1C4358]" /></div>
                <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#252221]">{s.name}</div><div className="text-xs text-muted-foreground">{s.domain || "No domain set"}</div></div>
                <Button size="sm" variant="outline" onClick={() => showSnippet(s)} data-testid="site-snippet-button"><Code2 size={14} className="mr-1.5" /> Install</Button>
                <button onClick={() => removeSite(s)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="site-delete-button" aria-label="Remove"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-site-dialog">
          <DialogHeader><DialogTitle>Connect a website</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Website name</Label><Input value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })} placeholder="MovingMyMama.com" data-testid="site-name-input" /></div>
            <div><Label className="text-sm">Domain (optional)</Label><Input value={newSite.domain} onChange={(e) => setNewSite({ ...newSite, domain: e.target.value })} placeholder="movingmymama.com" data-testid="site-domain-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={createSite} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="site-save-button">Connect</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!snippet} onOpenChange={(o) => !o && setSnippet(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="site-snippet-dialog">
          <DialogHeader><DialogTitle>Install tracking on {snippet?.site?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Paste this snippet before the closing &lt;/body&gt; tag on every page of your website.</p>
          <CopyBox value={snippet?.snippet || ""} testid="site-snippet-copy" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
